/**
 * Seleccion de la interfaz y construccion de acciones.
 *
 * Todo lo de este fichero es **puro**: no toca React, ni la red, ni el reloj.
 * Su unico trabajo es traducir «que ha pulsado el jugador» a una `PlayerAction`
 * valida, que es exactamente la parte que merece la pena probar.
 *
 * Aqui **no** se decide legalidad. Que una accion sea legal lo dice el servidor,
 * y este fichero no lo adivina: como mucho sabe si la seleccion esta lo bastante
 * completa como para poder construir el mensaje.
 */

import type { FieldLineId, FieldSlotIndex, InstanceId, PlayerAction } from '@tcg/game-rules';

/** Una posicion del Campo tal y como la senala la interfaz. */
export interface SlotRef {
  readonly line: FieldLineId;
  readonly index: FieldSlotIndex;
}

/**
 * Que tiene seleccionado el jugador ahora mismo.
 *
 * Es estado **de interfaz**, no de juego: se pierde al recargar y no le importa
 * a nadie mas.
 */
export interface Selection {
  /** Carta de la mano propia. */
  readonly handCard: InstanceId | null;
  /** Ofrenda del Altar propio. */
  readonly altarOffer: InstanceId | null;
  /** Invocacion propia del Campo. */
  readonly fieldCard: InstanceId | null;
  /** Espacio del Campo propio. */
  readonly slot: SlotRef | null;
  /** Carta enemiga senalada como objetivo. */
  readonly enemyCard: InstanceId | null;
}

export const EMPTY_SELECTION: Selection = {
  handCard: null,
  altarOffer: null,
  fieldCard: null,
  slot: null,
  enemyCard: null,
};

/** Las acciones que esta interfaz sabe construir. */
export const SUPPORTED_ACTIONS = [
  'playOffer',
  'exhaustOffer',
  'playInvocation',
  'moveInvocation',
  'attackCard',
  'attackPlayer',
  'endTurn',
] as const;

export type SupportedAction = (typeof SUPPORTED_ACTIONS)[number];

/**
 * Construye la accion, o `null` si la seleccion todavia no basta.
 *
 * Devolver `null` sirve para apagar un boton, **no** para juzgar la jugada: una
 * seleccion completa puede seguir siendo ilegal, y de eso responde el servidor.
 */
export function buildAction(kind: SupportedAction, selection: Selection): PlayerAction | null {
  switch (kind) {
    case 'playOffer':
      return selection.handCard === null
        ? null
        : { type: 'playOffer', instanceId: selection.handCard };

    case 'exhaustOffer':
      return selection.altarOffer === null
        ? null
        : { type: 'exhaustOffer', instanceId: selection.altarOffer };

    case 'playInvocation':
      return selection.handCard === null || selection.slot === null
        ? null
        : {
            type: 'playInvocation',
            instanceId: selection.handCard,
            destination: selection.slot,
          };

    case 'moveInvocation':
      return selection.fieldCard === null || selection.slot === null
        ? null
        : {
            type: 'moveInvocation',
            instanceId: selection.fieldCard,
            destination: selection.slot,
          };

    case 'attackCard':
      return selection.fieldCard === null || selection.enemyCard === null
        ? null
        : {
            type: 'attackCard',
            attackerInstanceId: selection.fieldCard,
            targetInstanceId: selection.enemyCard,
          };

    case 'attackPlayer':
      return selection.fieldCard === null
        ? null
        : { type: 'attackPlayer', attackerInstanceId: selection.fieldCard };

    case 'endTurn':
      return { type: 'endTurn' };
  }
}

/** Qué falta por seleccionar, para poder decirselo al jugador. */
export function describeMissing(kind: SupportedAction, selection: Selection): string | null {
  if (buildAction(kind, selection) !== null) {
    return null;
  }

  switch (kind) {
    case 'playOffer':
      return 'Selecciona una Ofrenda de tu mano.';
    case 'exhaustOffer':
      return 'Selecciona una Ofrenda de tu Altar.';
    case 'playInvocation':
      return selection.handCard === null
        ? 'Selecciona una Invocacion de tu mano.'
        : 'Selecciona un espacio libre de tu Campo.';
    case 'moveInvocation':
      return selection.fieldCard === null
        ? 'Selecciona una Invocacion de tu Campo.'
        : 'Selecciona el espacio de destino.';
    case 'attackCard':
      return selection.fieldCard === null
        ? 'Selecciona tu atacante.'
        : 'Selecciona una Invocacion enemiga.';
    case 'attackPlayer':
      return 'Selecciona tu atacante.';
    case 'endTurn':
      return null;
  }
}

/** Limpia la seleccion tras enviar una accion. */
export function clearSelection(): Selection {
  return EMPTY_SELECTION;
}
